import { BuildOutlined } from '@ant-design/icons';
import { FieldsetTitle } from '../../../components/professor_area/FieldsetTitle';
import { Form, FormProps, Switch } from 'antd';
import { getTenant } from 'services/tenants';
import type { IGraduateCourseShow } from 'interfaces/graduate_courses';

export interface GraduateCourseFeaturesPatch {
  code?: string;
  has_vade_mecum?: boolean;
  media?: { sso_qc?: boolean; forum?: boolean };
}

interface TabFeaturesProps {
  formProps: FormProps;
  record?: IGraduateCourseShow;
  onFinish: (values: GraduateCourseFeaturesPatch) => void;
}

export const TabFeatures = ({
  formProps,
  record,
  onFinish
}: TabFeaturesProps) => {
  // Each switch saves on its own. The PATCH carries only that switch: the Atlas
  // deep-merges media, so two quick clicks cannot undo each other whatever order
  // their requests land in. The form store is updated too, because it is shared
  // with the other tabs and their Save sends the whole form.
  const saveFeature = (patch: GraduateCourseFeaturesPatch) => {
    formProps.form?.setFieldsValue(patch);
    // The Atlas requires code on every update of a course that has one.
    onFinish(record?.code ? { ...patch, code: record.code } : patch);
  };

  const handleSwitchChange = (checked: boolean) =>
    saveFeature({ has_vade_mecum: checked });

  const handleSSOQCChange = (checked: boolean) =>
    saveFeature({ media: { sso_qc: checked } });

  const handleForumChange = (checked: boolean) =>
    saveFeature({ media: { forum: checked } });

  const tenant = getTenant();
  const ssoQcDisabled = tenant.disabledFeatures?.includes('sso_qc');
  const forumsDisabled = tenant.disabledFeatures?.includes('forums');

  return (
    <Form {...formProps} layout="vertical">
      <fieldset>
        <FieldsetTitle
          title="Funcionalidades do curso"
          icon={BuildOutlined}
          hint="Ative funcionalidades que estarão disponíveis para alunos deste curso"
        />
        <div className="pl-6">
          <Form.Item
            name="has_vade_mecum"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
          >
            <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
              <div>
                <div className="font-semibold text-base mb-1">
                  Vade Mecum Digital
                </div>
                <div className="text-gray-500 text-sm">
                  Biblioteca digital completa pra consulta da constituição, leis
                  e códigos
                </div>
              </div>
              <Switch
                defaultChecked={record?.has_vade_mecum}
                onChange={handleSwitchChange}
              />
            </div>
          </Form.Item>
          {!ssoQcDisabled && (
            <Form.Item
              name={['media', 'sso_qc']}
              valuePropName="checked"
              style={{ marginBottom: 0, marginTop: 16 }}
            >
              <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                <div>
                  <div className="font-semibold text-base mb-1">
                    Acesso a questões no Qconcursos
                  </div>
                  <div className="text-gray-500 text-sm">
                    Acesso automático a mesa de estudos e o banco de questões do
                    Qconcursos, sem precisar de novo login (SSO).
                  </div>
                </div>
                <Switch
                  defaultChecked={!!record?.media?.sso_qc}
                  onChange={handleSSOQCChange}
                />
              </div>
            </Form.Item>
          )}
          {!forumsDisabled && (
            <Form.Item
              name={['media', 'forum']}
              valuePropName="checked"
              style={{ marginBottom: 0, marginTop: 16 }}
            >
              <div className="flex items-center justify-between bg-white rounded-lg shadow p-5 border">
                <div>
                  <div className="font-semibold text-base mb-1">Fórum</div>
                  <div className="text-gray-500 text-sm">
                    Fórum do curso e fórum das turmas. Desligado, os alunos
                    deste curso deixam de ver o fórum.
                  </div>
                </div>
                {/* Courses created before this switch have no media.forum and keep the forum on. */}
                <Switch
                  defaultChecked={record?.media?.forum !== false}
                  onChange={handleForumChange}
                />
              </div>
            </Form.Item>
          )}
        </div>
      </fieldset>
    </Form>
  );
};
